import React from 'react';

/**
 * Componente para mostrar el badge de origen de datos
 * @param {Object} dataItem - El item de datos que puede contener la propiedad 'source'
 * @param {Object} options - Opciones de configuración del badge
 * @param {string} options.position - Posición del badge ('bottom-right', 'top-right', 'bottom-left', 'top-left')
 * @param {string} options.size - Tamaño del badge ('small', 'medium', 'large')
 * @param {string} options.variant - Variante de estilo ('muted', 'primary', 'secondary')
 * @param {boolean} options.showTooltip - Si mostrar tooltip con información adicional
 * @param {Object} options.customMapping - Mapeo personalizado de fuentes
 * @param {Array} options.excludeSources - Array de fuentes a excluir del badge (ej: ['retail', 'manual'])
 */
const DataSourceBadge = ({ 
  dataItem, 
  options = {} 
}) => {
  // Configuración por defecto
  const {
    position = 'bottom-right',
    size = 'small',
    variant = 'muted',
    showTooltip = true,
    customMapping = null,
    excludeSources = ['retail']
  } = options;

  // Mapeo centralizado de fuentes de datos
  const defaultMapping = {
    'siisa': 'SIISA',
    'manual': 'Manual',
    'bcra': 'BCRA',
    'afip': 'AFIP',
    'external': 'Externo',
    'system': 'Sistema',
    'user': 'Usuario',
    'api': 'API',
    'database': 'Base de Datos',
    'import': 'Importación',
    'cirenio': 'Cirenio',
    'retail': 'Retail'
  };

  // Usar mapeo personalizado si se proporciona, sino el por defecto
  const dataSourceMapping = customMapping || defaultMapping;

  // Función para obtener el nombre legible de la fuente
  const getDataSourceLabel = (source) => {
    if (!source) return null;
    const normalizedSource = source.toLowerCase().trim();
    return dataSourceMapping[normalizedSource] || source;
  };

  // Si no hay datos o no hay source, no renderizar nada
  if (!dataItem || !dataItem.source) return null;
  
  // Verificar si la fuente está en la lista de exclusión
  const normalizedSource = dataItem.source.toLowerCase().trim();
  if (excludeSources.includes(normalizedSource)) return null;
  
  const sourceLabel = getDataSourceLabel(dataItem.source);
  if (!sourceLabel) return null;

  // Configuración de posicionamiento
  const getPositionStyles = () => {
    const baseStyles = {
      position: 'absolute',
      fontSize: size === 'small' ? '0.65rem' : size === 'medium' ? '0.75rem' : '0.85rem',
      fontWeight: 'normal',
      backgroundColor: 'transparent'
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: '8px', right: '8px' };
      case 'top-left':
        return { ...baseStyles, top: '8px', left: '8px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '8px', left: '8px' };
      case 'bottom-right':
      default:
        return { ...baseStyles, bottom: '8px', right: '8px' };
    }
  };

  // Configuración de variantes de estilo
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return 'badge text-primary border border-primary';
      case 'secondary':
        return 'badge text-secondary border border-secondary';
      case 'muted':
      default:
        return 'badge text-muted border border-muted';
    }
  };

  // Información adicional para el tooltip
  const getTooltipContent = () => {
    if (!showTooltip) return null;
    
    const additionalInfo = [];
    if (dataItem.created_at) {
      additionalInfo.push(`Creado: ${dataItem.created_at}`);
    }
    if (dataItem.updated_at) {
      additionalInfo.push(`Actualizado: ${dataItem.updated_at}`);
    }
    
    let tooltipText = `Dato proveniente de: ${sourceLabel}`;
    if (additionalInfo.length > 0) {
      tooltipText += `\n${additionalInfo.join('\n')}`;
    }
    
    return tooltipText;
  };

  return (
    <span 
      className={getVariantStyles()}
      style={getPositionStyles()}
      title={getTooltipContent()}
      data-testid="data-source-badge"
    >
      {sourceLabel}
    </span>
  );
};

export default DataSourceBadge;
